import {
	CheckCircleOutlined,
	CloudSyncOutlined,
	CloudUploadOutlined,
	DeleteOutlined,
	EditOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Empty,
	Popconfirm,
	Space,
	Spin,
	Table,
	Tag,
	Tooltip,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { syncTPVs } from "../../services/tpvSyncService";
import { deleteTPV, getAllTPVs } from "../../services/tpvService";
import { getSyncConfig } from "../../services/syncService";

const TPVList = ({ refreshTrigger, onEditTPV }) => {
	const [tpvs, setTPVs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [syncingAll, setSyncingAll] = useState(false);
	const isMobile = window.innerWidth < 768;
	const [config, setConfig] = useState({
		interval: 30,
		intervalUnit: "segundos",
	});
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
	});

	// Cargar la configuración al inicio
	useEffect(() => {
		const fetchConfig = async () => {
			try {
				const syncConfig = await getSyncConfig();
				setConfig(syncConfig);
			} catch (error) {
				console.error("Error al cargar configuración:", error);
			}
		};

		fetchConfig();
	}, []);

	// Cargar TPVs al inicio y cuando cambie el trigger
	useEffect(() => {
		loadTPVs();
	}, [refreshTrigger]);

	// Actualizar la lista según el intervalo configurado
	useEffect(() => {
		const interval = setInterval(() => {
			loadTPVs();
		}, config.interval * (config.intervalUnit === "segundos" ? 1000 : config.intervalUnit === "minutos" ? 60000 : 3600000)); // Convertir a milisegundos

		// Limpiar intervalo al desmontar
		return () => clearInterval(interval);
	}, [config]);

	const loadTPVs = async () => {
		try {
			console.log("Cargando TPVs...");
			setLoading(true);
			const tpvList = await getAllTPVs();
			setTPVs(tpvList);
		} catch (error) {
			window.messageApi.error("Error al cargar TPVs: " + error.message);
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (id) => {
		try {
			await deleteTPV(id);
			// Actualizar la lista de TPVs
			setTPVs(tpvs.filter((tpv) => tpv.id !== id));
			window.messageApi.success("TPV eliminado correctamente");
		} catch (error) {
			window.messageApi.error("Error al eliminar TPV: " + error.message);
		}
	};

	const handleEdit = (tpv) => {
		if (onEditTPV) {
			onEditTPV(tpv);
		}
	};

	// Renderizar el estado de sincronización
	const renderSyncStatus = (pendingSync, lastSynced) => {
		if (pendingSync === 1) {
			return (
				<Tooltip title="Pendiente de sincronizar">
					<Tag icon={<CloudUploadOutlined />} color="warning">
						Pendiente
					</Tag>
				</Tooltip>
			);
		} else {
			const syncDate = lastSynced
				? new Date(lastSynced).toLocaleString()
				: "Nunca";

			return (
				<Tooltip title={`Última sincronización: ${syncDate}`}>
					<Tag icon={<CheckCircleOutlined />} color="success">
						Sincronizado
					</Tag>
				</Tooltip>
			);
		}
	};

	// Sincronizar tiendas de esta pantalla
	const handleSyncAll = async () => {
		try {
			setSyncingAll(true);
			await syncTPVs();
			// Recargar la lista para reflejar los cambios
			await loadTPVs();
			window.messageApi.success("Tiendas sincronizadas correctamente");
		} catch (error) {
			window.messageApi.error(
				"Error sincronizando tiendas: " + error.message
			);
		} finally {
			setSyncingAll(false);
		}
	};

	const columns = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
			responsive: ["lg"],
		},
		{
			title: "No.",
			dataIndex: "No",
			key: "No",
			render: (No) => No || "Pendiente",
		},
		{
			title: "Nombre",
			dataIndex: "nombre",
			key: "nombre",
		},
		{
			title: "Número de cédula/RUC/Passaporte",
			dataIndex: "nifCif",
			key: "nifCif",
			responsive: ["md"],
		},
		{
			title: "Ciudad",
			dataIndex: "ciudad",
			key: "ciudad",
			responsive: ["lg"],
		},
		{
			title: "Teléfono",
			dataIndex: "telefono",
			key: "telefono",
			responsive: ["md"],
		},
		{
			title: "N° celular",
			dataIndex: "movil",
			key: "movil",
			responsive: ["md"],
		},
		{
			title: "Email",
			dataIndex: "email",
			key: "email",
			responsive: ["lg"],
		},
		{
			title: "Vendedor",
			dataIndex: "cod_Vendedor",
			key: "cod_Vendedor",
			responsive: ["md"],
		},
		{
			title: "Margen de cierre",
			dataIndex: "margenCierre",
			key: "margenCierre",
			responsive: ["md"],
		},
		{
			title: "Estado",
			key: "syncStatus",
			render: (_, record) =>
				renderSyncStatus(record.pendingSync, record.lastSynced),
		},
		{
			title: "Acciones",
			key: "actions",
			render: (_, record) => (
				<Space size="middle">
					<Button
						type="primary"
						icon={<EditOutlined />}
						size="small"
						onClick={() => handleEdit(record)}
					>
						Editar
					</Button>
					<Popconfirm
						title="¿Está seguro de que desea eliminar este TPV?"
						onConfirm={() => handleDelete(record.id)}
						okText="Sí"
						cancelText="No"
					>
						<Button danger icon={<DeleteOutlined />} size="small">
							Eliminar
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	if (loading) {
		return <Spin size="large" />;
	}

	if (tpvs.length === 0) {
		return <Empty description="No hay tiendas registradas aún" />;
	}

	return (
		<div className="tpv-list-container">
			<Card
				title={isMobile ? "Tiendas" : "Tiendas Registradas"}
				extra={
					<Button
						icon={<CloudSyncOutlined />}
						onClick={handleSyncAll}
						loading={syncingAll}
						type="primary"
					>
						{isMobile ? "" : "Sincronizar tiendas"}
					</Button>
				}
			>
				<Table
					columns={columns}
					dataSource={tpvs.map((tpv) => ({ ...tpv, key: tpv.id }))}
					pagination={{
						...pagination,
						showSizeChanger: true,
						showQuickJumper: true,
						pageSizeOptions: ["5", "10", "20", "50"],
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} de ${total} elementos`,
					}}
					onChange={(newPagination) => setPagination(newPagination)}
					bordered
					size="middle"
					scroll={{ x: "max-content" }}
				/>
			</Card>
		</div>
	);
};

export default TPVList;
